import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import { ProductQA } from 'shared/dataAttributes'
import { colors, fonts } from 'shared/lib'
import IPrice from 'shared/types/IPrice'
import styled from 'styled-components'

import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import Badge from '../Badge'
import { InstantShipCallOut } from '../InstantShipCallOut'
import ModalPanelText from '../ModalPanelText'
import { t } from 'localization'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'

const TP = 'productTemplate.components.ProductPicker.InstantShipToggle'

const ShippingDaysText = styled.span`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
`

const Price = styled.p`
  margin: 0;
  ${fonts.SUBTITLE_1}
`

const Subtitle = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  margin: 0;
`

const SingleRadioButton = styled(RadioButton)`
  margin-bottom: 0;
`

interface IInstantShipToggleProps {
  newLowestPrice?: IPrice
  newInstantShipPrice?: IPrice
}

export const InstantShipToggle = ({
  newLowestPrice,
  newInstantShipPrice,
}: IInstantShipToggleProps) => {
  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const isNextDayHolidayShipping = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_HOLIDAY_SHIPPING_2022_NEXT_DAY,
  )
  const isStandardHolidayShipping = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_HOLIDAY_SHIPPING_2022_STANDARD,
  )
  const shippingDelays = useFeatureFlag(FeatureFlag.SHIPPING_DELAY)
  const {
    selectedConditionalProduct,
    selectedInstantShip,
    setInstantShip,
    setSelectedConditionalProduct,
    isApparel,
  } = useProductTemplateContext()
  const newNoConditionsProductSelected = !selectedConditionalProduct

  const { countryName } = useShoppingRegionContext()
  const isJapan = countryName === 'Japan'

  const handleSelectInstantShip = (isInstantShip: boolean) => {
    setInstantShip(isInstantShip)
    setSelectedConditionalProduct()
  }

  // remove this when we pull isMarketPricing
  if (!isMarketPricing && !newInstantShipPrice) {
    return null
  }

  if (!newLowestPrice && !newInstantShipPrice) {
    return null
  }

  return (
    <>
      {isMarketPricing && (
        <>
          <RadioGroup name="instantShipToggle">
            {!isApparel && newLowestPrice && (
              <RadioButton
                value="lowestPrice"
                title={<Price>{newLowestPrice.display}</Price>}
                tags={
                  <Badge
                    qaAttr={ProductQA.ShipBuyNewLowestPriceTag}
                    text={t(`${TP}.lowestPrice`, 'Lowest Price')}
                    rightAligned
                  />
                }
                subtitle={
                  shippingDelays ? (
                    <ShippingDaysText>
                      {t(
                        `${TP}.shippingDaysDelays`,
                        'This item will be sent to Flight Club first for verification',
                      )}
                    </ShippingDaysText>
                  ) : (
                    <ShippingDaysText>
                      {t(`${TP}.shppingDays`, 'Ships to us first for verification')}
                    </ShippingDaysText>
                  )
                }
                checked={newNoConditionsProductSelected && !selectedInstantShip}
                onRadioSelect={() => handleSelectInstantShip(false)}
                data-testid="lowestPriceOption"
                qaAttr={ProductQA.BuyNewLowestPriceOption}
              />
            )}
            {newInstantShipPrice && (
              <RadioButton
                value="instantShip"
                title={<Price>{newInstantShipPrice.display}</Price>}
                tags={
                  <Badge
                    qaAttr={ProductQA.ShipBuyNewInstantShipTag}
                    text={t(`${TP}.instantShip`, 'Fastest to you')}
                    badgeType="primary2"
                    rightAligned
                  />
                }
                subtitle={
                  shippingDelays ? (
                    <ShippingDaysText>
                      {t(
                        `${TP}.instantShippingDelays`,
                        'This item is pre-verified and ships instantly',
                      )}
                    </ShippingDaysText>
                  ) : (
                    <InstantShipCallOut />
                  )
                }
                checked={newNoConditionsProductSelected && selectedInstantShip}
                onRadioSelect={() => handleSelectInstantShip(true)}
                data-testid="instantShipOption"
                qaAttr={ProductQA.BuyNewInstantShipOption}
              />
            )}
          </RadioGroup>
          {!isJapan && newInstantShipPrice && !isStandardHolidayShipping && (
            <ModalPanelText data-qa={ProductQA.BuyNewShippingNote}>
              {t(
                `${TP}.buyNewShippingNote`,
                '*Not all items are eligible for Next Day shipping. Terms and conditions apply.',
              )}
            </ModalPanelText>
          )}
        </>
      )}
      {!isMarketPricing && newInstantShipPrice && (
        <SingleRadioButton
          value="instantShip"
          title={<Price>{newInstantShipPrice.display}</Price>}
          subtitle={
            <Subtitle>
              {t(`${TP}.buyNewInstantShipOption`, 'Pre-verified and ready to ship')}
            </Subtitle>
          }
          checked={newNoConditionsProductSelected && selectedInstantShip}
          onRadioSelect={() => handleSelectInstantShip(true)}
          data-testid="singleOption"
          qaAttr={ProductQA.BuyNewInstantShipOption}
        />
      )}
    </>
  )
}
